export type DesktopCalendarView = "week" | "month" | "list";
export type MobileCalendarView = "agenda" | "day" | "month";
// Kept for the legacy toolbar module until it is removed from older deployments.
export type CalendarView = "week" | "month";

export interface CalendarService {
  serviceId: number;
  name: string;
  colorHex?: string | null;
}

export interface CalendarServiceInstance {
  serviceInstanceId: number;
  serviceId: number;
  serviceName: string;
  serviceColorHex?: string | null;
  startDateTime: string;
  endDateTime: string;
  status: string;
  capacity?: number | null;
  bookedCount?: number;
  venue?: string | null;
  preRegistrationCampaignId?: number | null;
  preRegistrationCampaignName?: string | null;
  postRegistrationCampaignId?: number | null;
  postRegistrationCampaignName?: string | null;
}

export function startOfDay(value: Date) {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
}

export function startOfWeek(value: Date) {
  const date = startOfDay(value);
  const day = date.getDay();
  date.setDate(date.getDate() - (day === 0 ? 6 : day - 1));
  return date;
}

export function addDays(value: Date, days: number) {
  const date = new Date(value);
  date.setDate(date.getDate() + days);
  return date;
}

export function startOfMonthGrid(value: Date) {
  return startOfWeek(new Date(value.getFullYear(), value.getMonth(), 1));
}

export function endOfMonthGrid(value: Date) {
  return addDays(startOfWeek(new Date(value.getFullYear(), value.getMonth() + 1, 0)), 6);
}

export function sameDay(left: Date, right: Date) {
  return left.getFullYear() === right.getFullYear() && left.getMonth() === right.getMonth() && left.getDate() === right.getDate();
}

export function dateKey(value: Date | string) {
  const date = typeof value === "string" ? new Date(value) : value;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export function getDateRange(view: DesktopCalendarView | MobileCalendarView, selectedDate: Date) {
  const isMonth = view === "month";
  const start = isMonth ? startOfMonthGrid(selectedDate) : startOfWeek(selectedDate);
  const end = isMonth ? endOfMonthGrid(selectedDate) : addDays(start, 6);
  return {
    start,
    end,
    fromDate: dateKey(start),
    // The API compares start timestamps inclusively; the following midnight
    // ensures sessions throughout the final displayed day are returned.
    toDate: dateKey(addDays(end, 1)),
  };
}

export function formatTime(value: Date | string) {
  return new Date(value).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
}

export function formatTimeRange(instance: CalendarServiceInstance) {
  return `${formatTime(instance.startDateTime)} – ${formatTime(instance.endDateTime)}`;
}

export function formatLongDate(value: Date) {
  return value.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

export function formatWeekRange(value: Date, includeYear = true) {
  const start = startOfWeek(value);
  const end = addDays(start, 6);
  if (start.getMonth() === end.getMonth()) {
    return `${start.getDate()} – ${end.toLocaleDateString("en-GB", { day: "numeric", month: "long", ...(includeYear ? { year: "numeric" } : {}) })}`;
  }
  return `${start.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} – ${end.toLocaleDateString("en-GB", { day: "numeric", month: "long", ...(includeYear ? { year: "numeric" } : {}) })}`;
}

export function serviceColour(instance: CalendarServiceInstance) {
  return /^#[0-9a-f]{6}$/i.test(instance.serviceColorHex ?? "") ? instance.serviceColorHex! : "#037d41";
}

export function displayStatus(instance: CalendarServiceInstance) {
  if (instance.status && instance.status.toLowerCase() !== "scheduled") return instance.status;
  if (instance.capacity && (instance.bookedCount ?? 0) >= instance.capacity) return "Full";
  if (instance.capacity && (instance.bookedCount ?? 0) / instance.capacity >= 0.8) return "Nearly full";
  return "Open";
}

export function instancesForDay(instances: CalendarServiceInstance[], day: Date) {
  const key = dateKey(day);
  return instances
    .filter((instance) => dateKey(instance.startDateTime) === key)
    .sort((left, right) => new Date(left.startDateTime).getTime() - new Date(right.startDateTime).getTime());
}
